/**
 * Rozwijak — dostępne rozwijanie/zwijanie treści.
 *
 * Nagłówek jest JEDNYM `<button>` z `aria-expanded` i `aria-controls`, a treść
 * regionem powiązanym etykietą. Rozwijanie w cenniku partnera siedzi na
 * `onClick` w `<tr>` — czytnik ekranu nie ma tam czego kliknąć, klawiatura też
 * nie. Tu to naprawiamy raz, w jednym miejscu, i używamy wszędzie na stronie
 * oferty (pozycje, FAQ).
 *
 * Animacja idzie po `grid-template-rows` (0fr → 1fr), więc nie trzeba znać
 * wysokości treści; `motion-reduce:transition-none` wyłącza ją dla osób, które
 * ruchu nie chcą.
 */

import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

interface Props {
  id: string;
  otwarty: boolean;
  onToggle: () => void;
  /** Treść przycisku-nagłówka (cała szerokość). */
  naglowek: ReactNode;
  children: ReactNode;
  klasaPrzycisku?: string;
  klasaTresci?: string;
  /** Czy pokazać chevron po prawej stronie nagłówka. */
  chevron?: boolean;
  etykietaAria?: string;
}

export function Rozwijak({
  id,
  otwarty,
  onToggle,
  naglowek,
  children,
  klasaPrzycisku = '',
  klasaTresci = '',
  chevron = true,
  etykietaAria,
}: Props) {
  const idTresci = `${id}-tresc`;
  const idNaglowka = `${id}-naglowek`;
  return (
    <div>
      <button
        type="button"
        id={idNaglowka}
        aria-expanded={otwarty}
        aria-controls={idTresci}
        aria-label={etykietaAria}
        onClick={onToggle}
        className={`w-full text-left flex items-start gap-3 rounded-ids-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora-brand ${klasaPrzycisku}`}
      >
        <div className="min-w-0 flex-1">{naglowek}</div>
        {chevron && (
          <ChevronDown
            aria-hidden="true"
            className={`w-5 h-5 shrink-0 text-aurora-muted transition-transform duration-200 motion-reduce:transition-none ${
              otwarty ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>
      <div
        id={idTresci}
        role="region"
        aria-labelledby={idNaglowka}
        hidden={!otwarty}
        className={`grid transition-[grid-template-rows] duration-200 motion-reduce:transition-none ${
          otwarty ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        } ${klasaTresci}`}
      >
        <div className="min-h-0 overflow-hidden">{otwarty && children}</div>
      </div>
    </div>
  );
}
